import React from "react";

/**
 * "Security Replication" — the platform mirrors the security stack a customer
 * already runs, so the artwork animates as replication: each appliance in turn
 * pushes a packet across to the rack, and the rack unit that receives it writes
 * the payload down its port banks. After all four have landed they hold together
 * for a beat, which is the copy's "high-fidelity mirror".
 *
 * The Figma source (node 2251:56255) is one flat bitmap, so nothing in it could
 * move. Unlike the other rebuilt illustrations it did not need tracing: the rack
 * turned out to be regular geometry — four double-outlined chassis over a 5x3
 * lattice of port glyphs — and the labels are stroked stadiums with Avenir Next
 * text, so all of that is re-authored here as vector and animates per port. Only
 * the four appliance icons are still bitmap: they are organic linework and the
 * SIEM disc is a random dither that cannot be re-traced faithfully, while the
 * animation only ever moves them as whole tiles. `scripts/extract-security-
 * replication-icons.mjs` cuts them out of the export.
 *
 * Every number below is a stroke centre in the export's own 1388x541 space, read
 * off the pixels: a 2px stroke covering columns 13 and 14 has its centre at 14,
 * a 1px stroke covering column 31 has its centre at 31.5. Rebuilt against the
 * bitmap the whole illustration lands within a mean 3.7/255 per channel at the
 * ~640px the card renders it at, which is antialiasing on 1px linework.
 *
 * The four link routes are new. The export has no connectors at all — the rack
 * and the appliances just sit either side of an empty 205px gutter — so the
 * routes are drawn through that gutter and through the two empty horizontal
 * channels at y147 and y460, on the artwork's own 45deg grid.
 */

/** Artwork palette. */
const RED = "#EC1E3C";
/** Lit port / active chassis. Reads as the same red pushed to incandescence. */
const HOT = "#FF7C90";
const HEAD = "#FFFFFF";
const INK = "#EBECE6";
const BODY = "#14181D";
/** Idle link line: present enough to read as wiring, quiet enough to recede. */
const LINK = "#3D444B";

/** Chassis: the inner rect's y bounds per unit, then the outer rect's. */
const INNER_Y: [number, number][] = [
  [97, 178],
  [183, 263],
  [269, 349],
  [354, 435],
];
const OUTER_Y: [number, number][] = [
  [92, 183],
  [178, 269],
  [263, 354],
  [349, 440],
];
const INNER_X: [number, number] = [19, 518];
const OUTER_X: [number, number] = [14, 523];
const R_INNER = 3;
const R_OUTER = 6.5;
const CHASSIS_SW = 2;

/**
 * Port lattice, as pixel column bounds. Five columns each side of the empty
 * middle, at a 21px pitch; the odd 16px-wide column is the export's own rounding.
 */
const COLS: [number, number][] = [
  [31, 45],
  [52, 66],
  [73, 88],
  [94, 109],
  [115, 130],
  [406, 421],
  [427, 442],
  [449, 463],
  [470, 484],
  [491, 506],
];
/**
 * Row [top, height] per unit. The lattice drifts up to a pixel between units in
 * the export, so the rows are listed rather than derived from a pitch — that
 * jitter is what keeps the rebuild landing on the original's own sub-pixels.
 */
const ROWS: [number, number][][] = [
  [
    [101, 23],
    [125, 24],
    [150, 23],
  ],
  [
    [186, 24],
    [211, 23],
    [236, 23],
  ],
  [
    [272, 23],
    [297, 23],
    [321, 24],
  ],
  [
    [358, 23],
    [382, 24],
    [407, 23],
  ],
];
const PORT_SW = 1;
const PORT_R = 3;
/** The slot inside each port glyph, centred on its column. */
const HOLE = { w: 4, h: 13, r: 1, dy: 5 };

/** Mounting ear: two stroked outlines whose far side is buried under the chassis. */
const EAR = {
  sw: 2,
  outer: {
    dy: 27.5,
    r: 6.5,
    boxes: [
      [2, 20],
      [517, 536],
    ] as [number, number][],
  },
  inner: {
    dy: 22.5,
    r: 3.5,
    boxes: [
      [7, 25],
      [512, 530],
    ] as [number, number][],
  },
};

const unitCentre = (i: number) => (INNER_Y[i][0] + INNER_Y[i][1]) / 2;

/**
 * The four appliances, in the order they replicate — top-left, top-right,
 * bottom-left, bottom-right, which is also the order of the rack units they feed.
 *
 * Label metrics are fitted to each label's ink box as measured on the export,
 * against the font the page actually loads (Avenir Next Cyr through next/font,
 * so the family is `avenir` and not the system "Avenir Next" — they are close
 * relatives but ~5% apart in width, which is enough to matter over a 180-unit
 * label). Sizes and weights differ per label because the export's do: the
 * longest label was set smaller to fit its stadium. Every one lands within half
 * a unit of the original ink box.
 */
type Tile = {
  id: string;
  label: string;
  icon: { x: number; y: number; w: number; h: number };
  pill: { x: number; y: number; w: number; h: number };
  text: { x: number; y: number; fs: number; weight: number; tracking: number };
  /** Halo behind the icon, sized to the icon rather than to the tile. */
  halo: { cx: number; cy: number; rx: number; ry: number };
  /** Appliance to rack unit, 45deg elbows through the empty gutter. */
  route: string;
};

const TILES: Tile[] = [
  {
    id: "antivirus",
    label: "ANTI VIRUS",
    icon: { x: 800, y: 0, w: 112, h: 127 },
    pill: { x: 744, y: 167, w: 223, h: 60 },
    text: { x: 764.69, y: 207.56, fs: 31, weight: 400, tracking: 2 },
    halo: { cx: 856, cy: 63, rx: 92, ry: 84 },
    route: "M743 197 L657 197 L597 137 L537 137",
  },
  {
    id: "siem",
    label: "SIEM",
    icon: { x: 1235, y: 6, w: 137, h: 123 },
    pill: { x: 1241, y: 167, w: 123, h: 60 },
    text: { x: 1263.16, y: 208.6, fs: 33.5, weight: 600, tracking: 0.5 },
    halo: { cx: 1303, cy: 67, rx: 96, ry: 82 },
    // Out along the empty channel at y147, between the icons and their labels.
    route: "M1240 197 L1220 197 L1170 147 L613 147 L537 222.5",
  },
  {
    id: "ids",
    label: "IDS/IPS",
    icon: { x: 750, y: 317, w: 212, h: 127 },
    pill: { x: 777, y: 478, w: 157, h: 60 },
    text: { x: 796.23, y: 520.06, fs: 32.25, weight: 500, tracking: 1.375 },
    halo: { cx: 856, cy: 380, rx: 116, ry: 84 },
    // One long 45deg diagonal — the gutter is empty at every y, so it can cut it.
    route: "M776 508 L736 508 L537 308.5",
  },
  {
    id: "firewall",
    label: "Firewall",
    icon: { x: 1225, y: 316, w: 159, h: 119 },
    pill: { x: 1221, y: 479, w: 165, h: 60 },
    text: { x: 1241.99, y: 520.46, fs: 35, weight: 500, tracking: -0.125 },
    halo: { cx: 1304, cy: 375, rx: 92, ry: 80 },
    // Out along the empty channel at y460, under the magnifier.
    route: "M1220 509 L1200 509 L1151 460 L603 460 L537 394",
  },
];

const PILL_R = 30;
const PILL_SW = 2;
const SPRITE = (id: string) => `/images/OTSecurity/cyber-range/security-replication-icon-${id}.png`;

/** Polyline length, so a comet can be dashed to travel a route exactly once. */
const routeLength = (d: string) => {
  const pts = d
    .slice(1)
    .trim()
    .split(/\s*[ML]\s*/)
    .filter(Boolean)
    .map((p) => p.trim().split(/\s+/).map(Number));
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return len;
};

/**
 * One packet is a single dashed path whose dashes thin out towards the back, so
 * falling dash density reads as a fading trail — the same trick the network
 * traffic illustration uses. The pattern is written back to front, so the last
 * dash is the bright head and gets drawn again on top.
 */
const COMET = [2, 20, 4, 16, 6, 12, 8, 8, 10, 5, 22];
const COMET_SPAN = COMET.reduce((a, b) => a + b, 0);
/** Comet length, kept proportional to the route so short links aren't swamped. */
const cometScale = (len: number) => Math.min(110, Math.max(62, len * 0.28)) / COMET_SPAN;
/** Long enough that the pattern never repeats within a route. */
const NO_REPEAT = 100000;

/**
 * Port banks light from the outside in: the packet arrives at the right-hand ear,
 * so column 9 (rightmost) writes first and column 0 (leftmost) last.
 */
const WRITE_ORDER = [...COLS.keys()].reverse();
/**
 * Step between banks. Each bank's flash lasts ~0.35s, so at a 0.09s step four
 * banks are alight at once with a clear decay behind the leading one — that is
 * what makes it read as a wave rather than the whole unit flooding at once.
 */
const WRITE_STEP = 0.09;
const WRITE_START = 1.4;

type Vars = React.CSSProperties & Record<string, string | number>;

/** A port glyph: a rounded-rect outline with a slot, both 1px and inheriting `color`. */
const Port = ({ col, row }: { col: [number, number]; row: [number, number] }) => {
  const [x0, x1] = col;
  const [top, h] = row;
  const cx = (x0 + x1 + 1) / 2;
  const y = top + 0.5;
  return (
    <>
      <rect
        x={x0 + 0.5}
        y={y}
        width={x1 - x0}
        height={h}
        rx={PORT_R}
        fill="none"
        stroke="currentColor"
        strokeWidth={PORT_SW}
      />
      <rect
        x={cx - HOLE.w / 2}
        y={y + HOLE.dy}
        width={HOLE.w}
        height={HOLE.h}
        rx={HOLE.r}
        fill="none"
        stroke="currentColor"
        strokeWidth={PORT_SW}
      />
    </>
  );
};

const SecurityReplicationArt = () => (
  <>
    <svg
      viewBox="0 0 1388 541"
      role="img"
      aria-label="A server rack replicating four security appliances: anti virus, SIEM, IDS/IPS and firewall"
      className="h-auto w-full"
    >
      <defs>
        {/* Halo behind an appliance while it is the one being replicated. */}
        <radialGradient id="sr-halo">
          <stop offset="0" stopColor={RED} stopOpacity="0.30" />
          <stop offset="0.55" stopColor={RED} stopOpacity="0.10" />
          <stop offset="1" stopColor={RED} stopOpacity="0" />
        </radialGradient>
        {/* The light bar that travels a unit as its payload lands. Brightest at
            its leading (left) edge, since it is moving left. */}
        <linearGradient id="sr-sweep" x1="0" x2="1">
          <stop offset="0" stopColor={HOT} stopOpacity="0" />
          <stop offset="0.55" stopColor={HOT} stopOpacity="0.16" />
          <stop offset="0.86" stopColor={HOT} stopOpacity="0.30" />
          <stop offset="1" stopColor={HOT} stopOpacity="0" />
        </linearGradient>
        {/* Residue left in a unit once it holds a replica. */}
        <linearGradient id="sr-residue" x1="1" x2="0">
          <stop offset="0" stopColor={HOT} stopOpacity="0.16" />
          <stop offset="1" stopColor={HOT} stopOpacity="0.02" />
        </linearGradient>
        {INNER_Y.map(([y0, y1], i) => (
          <clipPath key={i} id={`sr-bay-${i}`}>
            <rect
              x={INNER_X[0]}
              y={y0}
              width={INNER_X[1] - INNER_X[0]}
              height={y1 - y0}
              rx={R_INNER}
            />
          </clipPath>
        ))}
      </defs>

      {/* Idle wiring. Drawn under everything so the appliances always read as
          plugged in, even between beats. */}
      {TILES.map((t) => (
        <path key={t.id} d={t.route} fill="none" stroke={LINK} strokeWidth={2.2} />
      ))}

      {/* The rack. */}
      {INNER_Y.map(([iy0, iy1], i) => {
        const [oy0, oy1] = OUTER_Y[i];
        const cy = unitCentre(i);
        const beat: Vars = { "--sr-beat": `${i * 2.6}s` };
        return (
          // `color` is set here, not on the port banks: a <use> clone inherits
          // from the <use> element only for properties the referenced content
          // does not set itself, so the banks have to stay uncoloured for the
          // relight copies to be able to override them.
          <g key={i} color={RED}>
            {[EAR.outer, EAR.inner].map((part, p) =>
              part.boxes.map(([bx0, bx1]) => (
                <rect
                  key={`${p}-${bx0}`}
                  x={bx0}
                  y={cy - part.dy}
                  width={bx1 - bx0}
                  height={part.dy * 2}
                  rx={part.r}
                  fill="none"
                  stroke={RED}
                  strokeWidth={EAR.sw}
                />
              ))
            )}
            {/* Buries the half of each ear that sits inside the chassis. */}
            <rect
              x={OUTER_X[0]}
              y={oy0}
              width={OUTER_X[1] - OUTER_X[0]}
              height={oy1 - oy0}
              rx={R_OUTER}
              fill={BODY}
            />

            {/* Payload landing: a light bar crossing the bay right to left, then
                a wash that stays while the unit holds its replica. */}
            <g clipPath={`url(#sr-bay-${i})`}>
              <rect
                className="sr-sweep"
                style={beat}
                x={0}
                y={iy0}
                width={260}
                height={iy1 - iy0}
                fill="url(#sr-sweep)"
              />
              <rect
                className="sr-residue"
                style={beat}
                x={INNER_X[0]}
                y={iy0}
                width={INNER_X[1] - INNER_X[0]}
                height={iy1 - iy0}
                fill="url(#sr-residue)"
              />
            </g>

            <rect
              x={OUTER_X[0]}
              y={oy0}
              width={OUTER_X[1] - OUTER_X[0]}
              height={oy1 - oy0}
              rx={R_OUTER}
              fill="none"
              stroke={RED}
              strokeWidth={CHASSIS_SW}
            />
            <rect
              x={INNER_X[0]}
              y={iy0}
              width={INNER_X[1] - INNER_X[0]}
              height={iy1 - iy0}
              rx={R_INNER}
              fill="none"
              stroke={RED}
              strokeWidth={CHASSIS_SW}
            />
            {/* Chassis brightening on the beat, as a second outline over the first. */}
            <rect
              className="sr-chassis"
              style={beat}
              x={OUTER_X[0]}
              y={oy0}
              width={OUTER_X[1] - OUTER_X[0]}
              height={oy1 - oy0}
              rx={R_OUTER}
              fill="none"
              stroke={HOT}
              strokeWidth={CHASSIS_SW}
            />

            {/* Port banks. Each column is grouped so a <use> can relight it
                without a second copy of the geometry in the markup. */}
            {COLS.map((col, c) => (
              <g key={c} id={`sr-bank-${i}-${c}`}>
                {ROWS[i].map((row) => (
                  <Port key={row[0]} col={col} row={row} />
                ))}
              </g>
            ))}
            {WRITE_ORDER.map((c, step) => (
              <use
                key={c}
                className="sr-write"
                href={`#sr-bank-${i}-${c}`}
                color={HOT}
                style={
                  {
                    ...beat,
                    "--sr-step": `${+(WRITE_START + step * WRITE_STEP).toFixed(3)}s`,
                  } as Vars
                }
              />
            ))}
          </g>
        );
      })}

      {/* The appliances. */}
      {TILES.map((t, i) => {
        const beat: Vars = { "--sr-beat": `${i * 2.6}s` };
        return (
          <g key={t.id}>
            <ellipse
              className="sr-halo"
              style={beat}
              cx={t.halo.cx}
              cy={t.halo.cy}
              rx={t.halo.rx}
              ry={t.halo.ry}
              fill="url(#sr-halo)"
            />
            <ellipse
              className="sr-ping"
              style={beat}
              cx={t.halo.cx}
              cy={t.halo.cy}
              rx={t.halo.rx}
              ry={t.halo.ry}
              fill="none"
              stroke={HOT}
              strokeWidth={2.2}
            />
            <image
              href={SPRITE(t.id)}
              x={t.icon.x}
              y={t.icon.y}
              width={t.icon.w}
              height={t.icon.h}
              preserveAspectRatio="none"
            />
            <rect
              x={t.pill.x}
              y={t.pill.y}
              width={t.pill.w}
              height={t.pill.h}
              rx={PILL_R}
              fill="none"
              stroke={RED}
              strokeWidth={PILL_SW}
            />
            {/* Label pill blooming while its appliance is the source. */}
            <rect
              className="sr-pill"
              style={beat}
              x={t.pill.x}
              y={t.pill.y}
              width={t.pill.w}
              height={t.pill.h}
              rx={PILL_R}
              fill={RED}
              fillOpacity="0.9"
              stroke={HOT}
              strokeWidth={PILL_SW}
            />
            <text
              x={t.text.x}
              y={t.text.y}
              fontFamily="var(--font-avenir)"
              fontSize={t.text.fs}
              fontWeight={t.text.weight}
              letterSpacing={t.text.tracking}
              fill={INK}
            >
              {t.label}
            </text>
          </g>
        );
      })}

      {/* Packets, on top of everything they travel over. */}
      {TILES.map((t, i) => {
        const len = routeLength(t.route);
        const scale = cometScale(len);
        const dashes = COMET.map((v) => +(v * scale).toFixed(2));
        const head = dashes[dashes.length - 1];
        const span = +(COMET_SPAN * scale).toFixed(2);
        const beat = { "--sr-beat": `${i * 2.6}s` };
        // The trail runs the whole pattern; the head is its last dash drawn
        // again, so its offsets are the trail's shifted by the pattern's lead.
        const trail: Vars = {
          ...beat,
          "--sr-o0": span,
          "--sr-o1": +(-len).toFixed(2),
          strokeDasharray: `${dashes.join(" ")} ${NO_REPEAT}`,
        };
        const headStyle: Vars = {
          ...beat,
          "--sr-o0": head,
          "--sr-o1": +(-len - span + head).toFixed(2),
          strokeDasharray: `${head} ${NO_REPEAT}`,
        };
        return (
          <g key={t.id} color={HOT}>
            <path className="sr-packet sr-glow" d={t.route} style={headStyle} />
            <path className="sr-packet sr-trail" d={t.route} style={trail} />
            <path
              className="sr-packet sr-head"
              d={t.route}
              style={{ ...headStyle, stroke: HEAD }}
            />
          </g>
        );
      })}
    </svg>

    <style jsx>{`
      /* One 12s master period for everything: four 2.6s beats, then 1.6s where
         all four units hold their replica together. Each element runs the full
         12s and states its own beat as a delay, so nothing can drift out of step
         and the percentages below are all read against the same clock.
           beat n starts at        n * 2.6s
           halo, pill              0.00 - 2.30s   (0.00% - 19.17%)
           packet                  0.20 - 1.45s   (1.67% - 12.08%)
           light bar               1.35 - 2.00s  (11.25% - 16.67%)
           port write              1.45 - 2.25s  (12.08% - 18.75%)
           chassis                 1.30 - 2.10s  (10.83% - 17.50%)
           residue                 1.60 - 2.60s  (13.33% - 21.67%) */
      .sr-halo,
      .sr-ping,
      .sr-pill,
      .sr-packet,
      .sr-sweep,
      .sr-residue,
      .sr-chassis,
      .sr-write {
        animation-duration: 12s;
        animation-delay: var(--sr-beat);
        animation-iteration-count: infinite;
        animation-fill-mode: both;
      }

      /* The appliance being replicated lifts out of the dark and pings once. */
      @keyframes srHalo {
        0%,
        19.17%,
        100% {
          opacity: 0;
        }
        3%,
        16% {
          opacity: 1;
        }
      }
      .sr-halo {
        opacity: 0;
        animation-name: srHalo;
        animation-timing-function: ease-in-out;
      }

      @keyframes srPing {
        0% {
          opacity: 0;
          transform: scale(0.72);
        }
        1.5% {
          opacity: 0.42;
        }
        5.5%,
        100% {
          opacity: 0;
          transform: scale(1.12);
        }
      }
      .sr-ping {
        opacity: 0;
        transform-box: fill-box;
        transform-origin: center;
        animation-name: srPing;
        animation-timing-function: ease-out;
      }

      /* The label reads as selected for the length of its beat. */
      @keyframes srPill {
        0%,
        19.17%,
        100% {
          opacity: 0;
        }
        2.5%,
        17% {
          opacity: 0.22;
        }
      }
      .sr-pill {
        opacity: 0;
        animation-name: srPill;
        animation-timing-function: ease-in-out;
      }

      /* Packet: one pass per cycle, invisible either side of it. The dash offset
         carries it from before the route's start to past its end, so it never
         has to be hidden mid-travel. */
      @keyframes srPacket {
        0%,
        1.67% {
          stroke-dashoffset: var(--sr-o0);
          opacity: 0;
        }
        3% {
          opacity: 1;
        }
        11% {
          opacity: 1;
        }
        12.08%,
        100% {
          stroke-dashoffset: var(--sr-o1);
          opacity: 0;
        }
      }
      .sr-packet {
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        opacity: 0;
        animation-name: srPacket;
        animation-timing-function: linear;
      }
      /* Widening the same dash costs less than a blur and survives being scaled
         down on mobile. */
      .sr-glow {
        stroke-width: 11;
        opacity: 0.18;
      }
      .sr-trail {
        stroke-width: 2.6;
      }
      .sr-head {
        stroke-width: 4;
      }

      /* The payload crossing the bay, right to left, from the ear the packet
         arrived at. Travel is stated in artwork units: 545 clears the bay. */
      @keyframes srSweep {
        0%,
        11.25% {
          opacity: 0;
          transform: translateX(545px);
        }
        12% {
          opacity: 1;
        }
        16% {
          opacity: 1;
        }
        16.67%,
        100% {
          opacity: 0;
          transform: translateX(-260px);
        }
      }
      .sr-sweep {
        opacity: 0;
        animation-name: srSweep;
        animation-timing-function: linear;
      }

      /* Once written, a unit keeps a wash until the cycle restarts — so by the
         last beat all four are lit at once, which is the "mirror in sync" beat. */
      @keyframes srResidue {
        0%,
        13.33% {
          opacity: 0;
        }
        18% {
          opacity: 1;
        }
        95% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
      .sr-residue {
        opacity: 0;
        animation-name: srResidue;
        animation-timing-function: ease-out;
      }

      @keyframes srChassis {
        0%,
        10.83%,
        17.5%,
        100% {
          opacity: 0;
        }
        12.5%,
        15.5% {
          opacity: 0.85;
        }
      }
      .sr-chassis {
        opacity: 0;
        animation-name: srChassis;
        animation-timing-function: ease-in-out;
      }

      /* Port write. Each bank carries its own step on top of its unit's beat, so
         the ten columns land in turn across 0.675s. The pulse does not linger:
         what a written unit holds is the body wash, which runs off the unit's
         own beat and so keeps all four in step for the finale. */
      @keyframes srWrite {
        0% {
          opacity: 0;
        }
        0.5%,
        1.3% {
          opacity: 1;
        }
        2.9%,
        100% {
          opacity: 0;
        }
      }
      .sr-write {
        opacity: 0;
        animation-name: srWrite;
        animation-timing-function: ease-out;
        animation-delay: calc(var(--sr-beat) + var(--sr-step));
      }

      @media (prefers-reduced-motion: reduce) {
        /* Every animated layer here is drawn on top of the artwork, so hiding
           them leaves the illustration exactly as the export had it. */
        .sr-halo,
        .sr-ping,
        .sr-pill,
        .sr-packet,
        .sr-sweep,
        .sr-residue,
        .sr-chassis,
        .sr-write {
          animation: none;
          display: none;
        }
      }
    `}</style>
  </>
);

export default SecurityReplicationArt;
